/**
 * Centralised timestamp formatting utilities.
 *
 * Target formats:
 *   formatTimestamp     → "2m ago" (<24h), else "May 14, 11:28 AM" / "May 14, 2025, 11:28 AM" (local)
 *   formatTimestampFull → "May 14, 2026, 11:28:06 AM GMT+2" (verbose local — use for hover/title tooltips)
 *   formatDate          → "May 14, 2026"                    (date-only contexts, e.g. author chips)
 *   formatTime          → "11:28 AM"                        (time-only contexts, e.g. inline chat rows)
 */

// Split into two formatters so the output is "May 14, 2026, 11:28 AM"
// instead of "May 14, 2026 at 11:28 AM" (which Intl produces when
// both date and time parts are combined in a single formatter).
const DATETIME_DATE_PART = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

// Date part without a year — used for the compact/relative formatter when the
// value falls within the current calendar year.
const DATETIME_DATE_PART_NO_YEAR = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
});

// Compact time part (no seconds) for the default UI-facing timestamp.
const DATETIME_TIME_PART = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});

// Verbose time part (with seconds) for the hover/title tooltip.
const DATETIME_TIME_PART_FULL = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
});

// Offset-style zone label (e.g. "GMT+2", "GMT+0") for the hover/title tooltip.
// Use shortOffset rather than short so every environment gets a numeric offset instead of a
// locale abbreviation ("UTC" on a UTC host, "EDT"/"PST" on US hosts), which is ambiguous to
// readers in other regions. Tests asserting this zone segment must mirror `shortOffset`.
const TIME_ZONE_NAME = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'shortOffset',
});

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

const TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Coerce any common date representation to a JS Date, or null on failure.
 * Handles: ISO strings, unix-epoch numbers, Firestore {seconds} objects,
 * Firestore Timestamp instances (with toDate()), and native Date objects.
 */
export function toDate(value) {
    if (!value && value !== 0) return null;
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    if (typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
}

function formatZoneAbbreviation(d) {
    const part = TIME_ZONE_NAME.formatToParts(d).find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
}

/**
 * True while `value` is inside formatTimestamp's relative-phrasing window (<24h old, not
 * future). Lets live-ticking consumers (useRelativeTimeTick) know when a rendered label can
 * still change on its own vs. when it has aged into the static absolute format.
 */
export function isWithinRelativeWindow(value) {
    const d = toDate(value);
    if (!d) return false;
    const diffMs = Date.now() - d.getTime();
    return diffMs >= 0 && diffMs < DAY_MS;
}

/**
 * Compact datetime string. Relative phrasing for recent values ("just now",
 * "Xm ago", "Xh ago"); absolute local date+time beyond 24h old or for any
 * future value, since "ago" phrasing doesn't make sense for those.
 */
export function formatTimestamp(value, fallback = '–') {
    const d = toDate(value);
    if (!d) return fallback;

    const diffMs = Date.now() - d.getTime();
    if (diffMs >= 0 && diffMs < DAY_MS) {
        if (diffMs < MINUTE_MS) return 'just now';
        if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)}m ago`;
        return `${Math.floor(diffMs / HOUR_MS)}h ago`;
    }

    const sameYear = d.getFullYear() === new Date().getFullYear();
    const datePart = sameYear ? DATETIME_DATE_PART_NO_YEAR.format(d) : DATETIME_DATE_PART.format(d);
    return `${datePart}, ${DATETIME_TIME_PART.format(d)}`;
}

/**
 * Verbose datetime string: "May 14, 2026, 11:28:06 AM GMT+2"
 * Use for hover/title tooltips where the exact local time and zone matter.
 */
export function formatTimestampFull(value, fallback = '–') {
    const d = toDate(value);
    if (!d) return fallback;
    return `${DATETIME_DATE_PART.format(d)}, ${DATETIME_TIME_PART_FULL.format(d)} ${formatZoneAbbreviation(d)}`;
}

/**
 * Date-only string: "May 14, 2026"
 * Use for contexts where the time component is irrelevant (author chips, publication dates, etc.)
 */
export function formatDate(value, fallback = '–') {
    const d = toDate(value);
    return d ? DATE_FORMAT.format(d) : fallback;
}

/**
 * Time-only string: "11:28 AM"
 * Use for inline chat rows where the date is already implied by context.
 */
export function formatTime(value, fallback = '–') {
    const d = toDate(value);
    return d ? TIME_FORMAT.format(d) : fallback;
}
